import React, { useEffect, useState } from "react";
import { Box } from "@mui/system";
import { useDispatch, useSelector } from "react-redux";
import { Avatar, Skeleton, Typography } from "@mui/material";
import { useTheme } from "@emotion/react";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import useSWR, { useSWRConfig } from "swr";
import useSWRMutation from "swr/mutation";
import { useNavigate } from "react-router";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useInView } from "react-intersection-observer";
import useSWRInfinite from "swr/infinite";

import LoginFooter from "../LoginFooter";
import LoginHeader from "../LoginHeader";
import { getPlayerState } from "../../../../redux/features/playerSlice";
import UseWindowDimensions from "../../../../utils/windowSize";
import { fetcherPost } from "../../../../services/fetcher";
import {
  getSettingState,
  setLoadNavigation,
  setSnackbarMode,
  setSnackbarTitle,
} from "../../../../redux/features/settingSlice";
import CustomHelmet from "../../CustomHelmet";

const EditCategoryPage = () => {
  const [favCategory, setFavCategory] = useState([]);
  const theme = useTheme();
  const { height } = UseWindowDimensions();
  const { minimizePlayer } = useSelector(getPlayerState);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const tokenStorage = JSON.parse(localStorage.getItem("token")) || null;
  const { userData } = useSelector(getSettingState);
  const { mutate } = useSWRConfig();

  const { ref, inView } = useInView({
    /* Optional options */
    threshold: 0,
  });

  const { data: profile } = useSWR(() => {
    if (!userData) {
      return null;
    }
    return `${process.env.REACT_APP_API}/auth/profile/${userData}/`;
  });

  const {
    data: category,
    isLoading,
    isValidating,
    size,
    setSize,
  } = useSWRInfinite(
    (pageIndex, previousPageData) => {
      if (previousPageData && !previousPageData.results.length) return null;
      return `${process.env.REACT_APP_API}/content/categories/?page=${pageIndex + 1}`;
    },
    {
      revalidateFirstPage: false,
      revalidateAll: false,
    }
  );

  useEffect(() => {
    !isLoading && dispatch(setLoadNavigation(isValidating));
  }, [dispatch, isLoading, isValidating]);

  useEffect(() => {
    inView && moreInfinite();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  const moreInfinite = () => {
    const getNewPage = size + 1 <= category[0]?.total_pages;
    if (getNewPage) {
      setSize(size + 1);
      dispatch(setLoadNavigation(true));
    } else {
      setSize(category[0].total_pages);
    }
  };

  const dataList = () => {
    let dataArr = [];
    if (category) {
      // eslint-disable-next-line array-callback-return
      category.map((e) => {
        dataArr.push(...e.results);
      });
    }
    return dataArr;
  };

  const favIds = () => {
    let dataArr = [];
    // eslint-disable-next-line array-callback-return
    profile.favorite_categories.map((e) => {
      dataArr.push(e.pk);
    });

    return dataArr;
  };

  useEffect(() => {
    if (profile) {
      setFavCategory(favIds());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  const { trigger, isMutating } = useSWRMutation(
    [`${process.env.REACT_APP_API}/auth/profile/categories/update/`, tokenStorage],
    fetcherPost,
    {
      onSuccess: () => {
        dispatch(setSnackbarMode(true));
        dispatch(setSnackbarTitle("Your category has been updated"));
        mutate(`${process.env.REACT_APP_API}/auth/profile/${userData}/`);

        navigate("/");
      },
    }
  );

  const existCat = (id) => {
    return favCategory.indexOf(id) !== -1;
  };

  const handleClickCategory = (id) => {
    if (existCat(id)) {
      const filterFavCategory = favCategory.filter((e) => e !== id);
      setFavCategory(filterFavCategory);
    } else {
      setFavCategory([...favCategory, id]);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      await trigger({ ids: favCategory });
    } catch {}
  };

  return (
    <>
      <CustomHelmet title={`Edit category | Azad`} />

      <Box sx={{ height: "100%", backgroundColor: "white.main" }}>
        <LoginHeader type='editCategory' />
        <Box
          sx={{
            display: "flex",
            height: height + "px",
            alignItems: "end",
          }}
        >
          <Box sx={{ width: "100%", mb: "72px", mt: "96px" }}>
            <Box sx={{ px: "16px", mb: "12px" }}>
              <Typography
                sx={{
                  fontSize: "40px",
                  fontWeight: 700,
                  fontFamily: "Tahrir",
                  lineHeight: "40px",
                }}
              >
                Welcome To Azad
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                border: "2px solid",
                borderBottom: "3px solid",
                borderColor: "black.main",
                padding: "24px 16px 0 16px",
                borderRadius: "16px 16px 0 0",
              }}
            >
              <Typography
                sx={{ fontSize: "20px", lineHeight: "28px", mb: "10px" }}
              >
                Please select your favorite categories.
              </Typography>
              <Box sx={{ display: "flex" }}>
                <InfoOutlinedIcon />
                <Typography
                  sx={{
                    fontSize: "17px",
                    lineHeight: "28px",
                    mb: "16px",
                    color: "textField.main",
                    ml: "5px",
                  }}
                >
                  This helps us show you tables that match your interests.
                </Typography>
              </Box>

              <Box
                sx={{
                  height: height + -407 - (minimizePlayer ? 82 : 0) + "px",
                  overflowY: "auto",
                }}
              >
                <Grid2 container spacing={2}>
                  {(isLoading || !category) &&
                    Array(10)
                      .fill("")
                      .map((row, i) => {
                        return (
                          <Grid2 key={i} xxs={6}>
                            <Skeleton
                              width={"100%"}
                              height={"164px"}
                              variant='rounded'
                              sx={{ borderRadius: "16px" }}
                            />
                          </Grid2>
                        );
                      })}

                  {!isLoading &&
                    category &&
                    dataList().map((row) => {
                      return (
                        <Grid2 key={row.pk} xxs={6}>
                          <Box
                            onClick={() => handleClickCategory(row.pk)}
                            sx={{
                              position: "relative",
                              overflow: "hidden",
                              p: "3px",
                              cursor: "pointer",
                            }}
                          >
                            <Avatar
                              alt={row.name}
                              src={row.image}
                              sx={{
                                width: "100%",
                                height: "164px",
                                borderRadius: "16px",
                                boxShadow: `0px 0px 0px 2px ${theme.palette.black.main}`,
                              }}
                            />
                            <Typography
                              sx={{
                                fontFamily: "Tahrir",
                                fontWeight: 700,
                                fontSize: "32px",
                                lineHeight: "32px",
                                position: "absolute",
                                bottom: "12px",
                                left: "18px",
                                zIndex: 1,
                              }}
                            >
                              {row.name}
                            </Typography>
                            <Box
                              sx={{
                                position: "absolute",
                                backgroundColor: "whiteDisabled.main",
                                top: 2,
                                bottom: 2,
                                left: 2,
                                right: 2,
                                borderRadius: "16px",
                                opacity: existCat(row.pk) ? 0 : 0.555,
                              }}
                            ></Box>
                          </Box>
                        </Grid2>
                      );
                    })}
                  <Box component={"form"} onSubmit={handleSubmit}>
                    <LoginFooter type='editAvatar' disabled={isMutating} />
                  </Box>
                </Grid2>
                {!isLoading && category && (
                  <Box
                    ref={ref}
                    sx={{ display: "flex", justifyContent: "center" }}
                  ></Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default EditCategoryPage;
